import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    SetAccountParent as SetAccountParentMutation,
    SetAccountParentVariables as SetAccountParentMutationVariables,
} from 'nutshell-graphql-types';

import ACCOUNT_HIERARCHY_FRAGMENT from '../fragments/account-hierarchy-fragment.graphql';
import SET_ACCOUNT_PARENT from '../mutations/set-account-parent.graphql';

export const useSetAccountParent = (): {
    setAccountParent: (input: {
        accountId: string;
        parentAccountId: string;
        isAddingParent: boolean;
    }) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [setParent, {loading, error}] = useMutation<
        SetAccountParentMutation,
        SetAccountParentMutationVariables
    >(SET_ACCOUNT_PARENT);

    return {
        setAccountParent: (input: {
            accountId: string;
            parentAccountId: string;
            isAddingParent: boolean;
        }) => {
            const {accountId, parentAccountId, isAddingParent} = input;

            return setParent({
                variables: {
                    input: {
                        accountId,
                        parentAccountId,
                    },
                },
                update: (proxy, mutationResults) => {
                    const idToUpdate = isAddingParent ? accountId : parentAccountId;

                    const accountFragment = proxy.readFragment({
                        id: idToUpdate,
                        fragmentName: 'AccountHierarchyFragment',
                        fragment: ACCOUNT_HIERARCHY_FRAGMENT,
                    });

                    if (accountFragment) {
                        const result =
                            mutationResults &&
                            mutationResults.data &&
                            mutationResults.data.accountParentSet;

                        if (!result) return;

                        const updatedAccount = isAddingParent
                            ? result.account
                            : result.parentAccount;
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        mutationResults &&
                            mutationResults.data &&
                            mutationResults.data.accountParentSet &&
                            mutationResults.data.accountParentSet.account;

                        if (!updatedAccount) return;

                        proxy.writeFragment({
                            id: idToUpdate,
                            fragmentName: 'AccountHierarchyFragment',
                            fragment: ACCOUNT_HIERARCHY_FRAGMENT,
                            data: updatedAccount,
                        });
                    }
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
